(() => {
  "use strict";

  const normaliseSize = (value) => String(value || "").toLowerCase().replace(/\s+/g, " ").trim();
  const dimensionToken = (value) => String(value || "").toLowerCase().match(/\d+\s*[x×]\s*\d+/)?.[0]?.replace(/\s|×/g, "x") || "";

  const initMobileAccordions = (scope = document) => {
    scope.querySelectorAll("[data-ug-mobile-accordion]").forEach((accordion) => {
      if (accordion.dataset.ugReleaseInit === "true") return;
      accordion.dataset.ugReleaseInit = "true";
      const toggles = [...accordion.querySelectorAll("[data-ug-mobile-submenu-toggle]")];
      const setExpanded = (toggle, expanded) => {
        const submenu = document.getElementById(toggle.getAttribute("aria-controls"));
        if (!submenu) return;
        toggle.setAttribute("aria-expanded", String(expanded));
        submenu.hidden = !expanded;
      };
      toggles.forEach((toggle) => {
        setExpanded(toggle, false);
        toggle.addEventListener("click", () => {
          const expand = toggle.getAttribute("aria-expanded") !== "true";
          toggles.forEach((other) => setExpanded(other, false));
          setExpanded(toggle, expand);
        });
      });
    });
  };

  const initGalleries = (scope = document) => {
    scope.querySelectorAll("[data-ug-gallery]").forEach((gallery) => {
      if (gallery.dataset.ugReleaseInit === "true") return;
      gallery.dataset.ugReleaseInit = "true";
      const productScope = gallery.closest("[data-ug-product]");
      const items = [...gallery.querySelectorAll("[data-ug-gallery-item]")];
      const dialog = productScope?.querySelector("[data-ug-gallery-dialog]");
      const dialogImage = dialog?.querySelector(".ug-gallery-dialog__image");
      const caption = dialog?.querySelector("[data-ug-gallery-caption]");
      let activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains("is-variant-selected")));
      let returnFocus = null;

      const setImageSource = (image, src, alt) => {
        if (!image || !src) return;
        image.removeAttribute("srcset");
        image.removeAttribute("sizes");
        image.src = src;
        image.alt = alt || "";
      };

      const updateCaption = (alt) => {
        if (caption) caption.textContent = "Image " + (activeIndex + 1) + " of " + items.length + " · " + alt;
      };

      const select = (index) => {
        if (!items.length || !dialogImage) return;
        activeIndex = (index + items.length) % items.length;
        const item = items[activeIndex];
        const alt = item.dataset.ugAlt || "";
        setImageSource(dialogImage, item.dataset.ugFullSrc, alt);
        updateCaption(alt);
      };

      items.forEach((item, index) => item.addEventListener("click", () => {
        if (!dialog || !dialogImage) return;
        returnFocus = item;
        select(index);
        dialog.showModal();
        dialog.querySelector("[data-ug-gallery-close]")?.focus();
      }));
      dialog?.querySelector("[data-ug-gallery-close]")?.addEventListener("click", () => dialog.close());
      dialog?.querySelector("[data-ug-gallery-prev]")?.addEventListener("click", () => select(activeIndex - 1));
      dialog?.querySelector("[data-ug-gallery-next]")?.addEventListener("click", () => select(activeIndex + 1));
      dialog?.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog?.addEventListener("close", () => returnFocus?.focus());
    });
  };

  const normalizeAccessibleControls = (scope = document) => {
    scope.querySelectorAll('select#SortBy').forEach((select, index) => {
      const id = `SortBy-${index + 1}`;
      select.id = id;
      const container = select.closest('form, .facet-filters, .facets, .drawer') || select.parentElement;
      const label = container?.querySelector('label[for="SortBy"]');
      if (label) label.htmlFor = id;
      if (!select.getAttribute('aria-label') && !label) select.setAttribute('aria-label', 'Sort products');
    });
    scope.querySelectorAll('#FacetFiltersContainer').forEach((container, index) => {
      container.id = `FacetFiltersContainer-${index + 1}`;
    });
  };

  const initCompatible = (scope = document) => {
    scope.querySelectorAll("[data-ug-compatible]").forEach((section) => {
      if (section.dataset.ugReleaseInit === "true") return;
      section.dataset.ugReleaseInit = "true";
      const productScope = section.closest("[data-ug-product]");
      const mode = section.dataset.ugCompatibleMode;
      const choiceRequired = section.dataset.ugChoiceRequired || "Choose the size and options you need.";
      const matchUnavailable = section.dataset.ugMatchUnavailable || "The matching option is currently unavailable. Please contact the U&G team before choosing a substitute.";
      const allUnavailable = section.querySelector("[data-ug-compatible-all-unavailable]");

      const sync = (size) => {
        const target = normaliseSize(size || section.dataset.ugCurrentSize);
        const targetDimension = dimensionToken(target);
        let visibleCards = 0;
        let purchasableCards = 0;

        section.querySelectorAll("[data-ug-compatible-card]").forEach((card) => {
          const select = card.querySelector("[data-ug-compatible-select]");
          const button = card.querySelector("[data-ug-compatible-add]");
          const status = card.querySelector("[data-ug-compatible-status]");
          const price = card.querySelector("[data-ug-compatible-price]");
          if (!select) {
            card.hidden = false;
            visibleCards += 1;
            purchasableCards += button && !button.disabled ? 1 : 0;
            return;
          }

          if (mode === "general") {
            card.hidden = false;
            visibleCards += 1;
            if (button) {
              button.dataset.ugCompatibleValid = String(Boolean(select.value));
              if (button.dataset.ugBusy !== "true") button.disabled = !select.value;
            }
            if (status && !select.value) status.textContent = choiceRequired;
            if (select.value) purchasableCards += 1;
            return;
          }

          const options = [...select.options];
          const match = options.find((option) => {
            const optionSize = normaliseSize(option.dataset.ugSize);
            if (mode === "cushion-size") return targetDimension && dimensionToken(optionSize) === targetDimension;
            return optionSize === target;
          });

          if (!match) {
            card.hidden = false;
            visibleCards += 1;
            select.value = "";
            select.disabled = false;
            if (button) {
              button.dataset.ugQuickAdd = "";
              button.dataset.ugCompatibleValid = "false";
              if (button.dataset.ugBusy !== "true") button.disabled = true;
            }
            if (status) status.textContent = matchUnavailable;
            return;
          }
          select.value = match.value;
          const available = match.dataset.ugAvailable === "true" && !match.disabled;
          card.hidden = false;
          visibleCards += 1;
          select.disabled = false;
          if (button) {
            button.dataset.ugQuickAdd = match.value;
            button.dataset.ugCompatibleValid = String(available);
            if (button.dataset.ugBusy !== "true") button.disabled = !available;
          }
          if (price && match.dataset.ugPrice) {
            price.textContent = `R ${(Number(match.dataset.ugPrice) / 100).toFixed(2)}`;
          }
          if (status) status.textContent = available ? "" : choiceRequired;
          if (available) purchasableCards += 1;
        });

        if (allUnavailable) allUnavailable.hidden = visibleCards > 0 || purchasableCards > 0;
      };

      productScope?.addEventListener("ug:variant-change", (event) => sync(event.detail?.variant?.options?.[0]));
      sync();
    });
  };

  const initDesktopMenus = (scope = document) => {
    scope.querySelectorAll(".ug-nav--dynamic details").forEach((details) => {
      if (details.dataset.ugReleaseInit === "true") return;
      details.dataset.ugReleaseInit = "true";
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        document.querySelectorAll(".ug-nav--dynamic details[open]").forEach((other) => {
          if (other !== details) other.removeAttribute("open");
        });
      });
    });
  };

  const init = (scope = document) => {
    initMobileAccordions(scope);
    initGalleries(scope);
    initCompatible(scope);
    initDesktopMenus(scope);
    normalizeAccessibleControls(scope);
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
